/* eslint-disable react-doctor/no-inline-exhaustive-style */
"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import Link from "next/link";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Plate } from "@/components/plate-and-hairline/plate";
import { splitTitle } from "@/lib/plate-and-hairline";
import { useHeroRotation } from "@/hooks/useHeroRotation";
import { downloadCountAriaLabel } from "@/lib/format-count";
import { Mono } from "@/components/ui/tls/mono";
import { PlayerSeek, PlayerTime } from "@/components/ui/audiocn/player";
import { useAudioPlayerContext } from "@/contexts/audio-player-context";
import { useAudioLevel } from "@/hooks/useAudioLevel";
import { TPlay } from "@/components/ui/tls/t-play";
import { soundUrl } from "@/lib/slugify";
import { resolveHomeHeroMedia } from "@/lib/home-hero-media";
import { downloadRankPhrase } from "@/lib/homepage-rails";
// The install time is ONE number site-wide (nav pill, guide, HowTo JSON-LD).
// The headline states it, so it reads from the same source rather than
// hardcoding a second copy that can drift.
import { TESLA_FACTS } from "@/lib/tesla-facts";
import type { Sound } from "@/lib/supabase";
import { PANEL_CONTAINER_CLASS, PANEL_CONTAINER_STYLE } from "@/components/sections/shared/panel-grid";

interface HeroSectionProps {
  /** Slide one. Its own prop because it is the first thing server-rendered. */
  heroSound?: Sound;
  /**
   * The rotation set, most downloaded first. `heroSounds[0]` must be
   * `heroSound`, so the slide the server paints first matches the slide the
   * client shows first. Rotation is a client-side enhancement layered on top
   * of that. The page's `<h1>` does not live on any slide — see the stable
   * heading rendered above the slide stack below.
   */
  heroSounds?: Sound[];
  pinnedSlide?: { soundId: string; kicker: string };
  /** Site-wide install rank by sound id, so the line under the plate states it truthfully. */
  downloadRanks?: Record<string, number>;
}

/**
 * One slide's plate.
 *
 * None of the five slides is a real `<h1>`. A page with five h1s (four of them
 * `aria-hidden`) fails WCAG 2.4.6/1.3.1 for 4 of every 5 dwell states, because
 * `aria-hidden` removes the whole subtree, tag name included, from the
 * accessibility tree.
 *
 * Every slide instead carries `role="heading" aria-level={2}`, identically, so
 * whichever one is on screen is exposed to AT and the inactive four never are.
 *
 * Level TWO, not one, and the distinction is the whole point. The page's one
 * literal `<h1>` is the stable, non-rotating heading above the slide stack; it
 * describes what this PAGE is. A slide carries the name of one featured sound,
 * which is a thing on the page rather than the page itself. Marking both level
 * one exposed two competing h1s at once, so a reader navigating by heading met
 * "Tesla lock chime sounds" and "Iron Man Jarvis Defense Protocol" as peers,
 * with no way to tell which one owned the document.
 *
 * `PlateHeading` is unused here on purpose: its `Heading` wrapper always
 * emits `h1`/`h2`, and swapping which slide gets that tag as rotation
 * advances would mean unmounting/remounting the wrapper element under the
 * plate on every dwell change — exactly the re-solve jitter the "slides are
 * mounted once, forever" rule in `HeroSection` exists to avoid.
 */
function HeroPlate({ title, level }: { title: string; level?: number }) {
  const { display, remainder } = splitTitle(title);

  return (
    <div
      data-ls
      role="heading"
      aria-level={2}
        style={{
          color: "var(--tls-hero-fg)",
          maxWidth: 1180,
          fontFamily: "var(--ls-font), var(--tls-font-display)",
          "--ls-signal-color": "var(--tls-hero-signal)",
        } as React.CSSProperties}
    >
      <Plate as="div" text={display} usable={358} usableDesktop={1180} level={level} />
      {/* The original bytes, verbatim — the same SEO-safe reconstruction rule
          PlateHeading follows. Never re-assembled from the descriptor. */}
      {remainder ? (
        <span
          className="ls-trim ls-plate-descriptor"
          style={{ "--ls-descriptor-color": "var(--tls-hero-muted)" } as React.CSSProperties}
        >
          {remainder}
        </span>
      ) : null}
    </div>
  );
}

export default function HeroSection({ heroSound, heroSounds, pinnedSlide, downloadRanks }: HeroSectionProps) {
  const player = useAudioPlayerContext();
  const prefersReducedMotion = useReducedMotion();

  // The rotation set. Deduped on id, because the trending and popularity
  // queries can hand back the same sound twice and two identical slides read
  // as the rotation being broken. Slide one is pinned to `heroSound`, so the
  // client can never disagree with the server-rendered h1.
  const slides = useMemo(() => {
    const pool = heroSounds?.length ? heroSounds : heroSound ? [heroSound] : [];
    const seen = new Set<string>();
    const unique: Sound[] = [];
    for (const item of pool) {
      if (!item?.id || seen.has(item.id)) continue;
      seen.add(item.id);
      unique.push(item);
    }
    return unique;
  }, [heroSounds, heroSound]);

  const slideCount = slides.length;
  const rotates = slideCount > 1;

  // Transient holds. Focus is deliberately not one of them: focus inside the
  // hero is treated as engagement, which is strictly stronger — it stops
  // rotation for the session rather than for as long as focus stays.
  const [pointerInside, setPointerInside] = useState(false);
  const [tabHidden, setTabHidden] = useState(false);
  // Keep the server-rendered control honest during the small window before
  // this client island has attached its handlers. Without this guard, a very
  // early tap can land on the static HTML and disappear instead of starting
  // the preview.
  const [isHydrated, setIsHydrated] = useState(false);

  const [reservedPlateHeight, setReservedPlateHeight] = useState(0);
  const plateBoxRefs = useRef<Array<HTMLDivElement | null>>([]);

  const currentPlayingId = player.currentSound?.id;

  const rotation = useHeroRotation({
    count: slideCount,
    reducedMotion: Boolean(prefersReducedMotion),
    held:
      pointerInside ||
      tabHidden ||
      // Never rotate out from under audio the visitor is listening to.
      (player.isPlaying && slides.some(item => item.id === currentPlayingId)),
  });

  const { index, engage } = rotation;
  const sound = slides[index] ?? heroSound;
  const isHeroPlaying = Boolean(sound?.id) && currentPlayingId === sound?.id && player.isPlaying;

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- hydration readiness is a client lifecycle boundary for the server-rendered button.
    setIsHydrated(true);
    if (typeof document === "undefined") return;
    const sync = () => setTabHidden(document.visibilityState === "hidden");
    sync();
    document.addEventListener("visibilitychange", sync);
    return () => document.removeEventListener("visibilitychange", sync);
  }, []);

  /**
   * Reserve the tallest slide's plate height on every slide.
   *
   * The plate solver sizes each title independently, so slide 3 can be one
   * line where slide 1 is three. Without a reservation the player row and the
   * CTA row would sit at a different y on every slide and the whole panel
   * would jitter as it rotates. Measuring is the only honest way to get the
   * number: the height budget (260/230) is a ceiling, not a prediction, and
   * pinning every hero to the ceiling would leave a short title floating in a
   * quarter-screen of nothing.
   *
   * The measured node is the INNER box, which carries no min-height, so the
   * reservation can shrink as well as grow rather than ratcheting upward.
   */
  useEffect(() => {
    if (!rotates || typeof ResizeObserver === "undefined") return;
    const nodes = plateBoxRefs.current.filter(Boolean) as HTMLDivElement[];
    if (nodes.length === 0) return;

    let frame = 0;
    const recompute = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const tallest = nodes.reduce(
          (max, node) => Math.max(max, Math.ceil(node.getBoundingClientRect().height)),
          0
        );
        setReservedPlateHeight(current => (current === tallest ? current : tallest));
      });
    };

    const observer = new ResizeObserver(recompute);
    nodes.forEach(node => observer.observe(node));
    recompute();

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [rotates, slideCount]);

  // Rule 3: the signal lives inside the type, and §11.3 defines it as an
  // envelope follower (120ms attack / 320ms release, hard-limited to three
  // direction changes a second and a 40% luminance range) — not a number we
  // pick. Under reduced motion the hook freezes at a representative frame
  // rather than dropping the fill, because the signal is information, not
  // decoration. Only the slide on screen is ever handed a level.
  const heroLevel = useAudioLevel(
    player.audioElement,
    isHeroPlaying,
    Boolean(prefersReducedMotion)
  );

  // Always acts on the sound currently on the plate: each slide owns its own
  // control, and only the slide on screen is interactive.
  const handlePlay = useCallback(
    (target: Sound) => {
      engage();
      if (player.currentSound?.id === target.id && player.isPlaying) player.pause();
      else player.play(target);
    },
    [engage, player]
  );

  const announcement =
    rotation.announceIndex === null
      ? ""
      : `Sound ${rotation.announceIndex + 1} of ${slideCount}, ${slides[rotation.announceIndex]?.title ?? ""}`;

  /**
   * Two overlapping layers, opacity only.
   *
   * Every slide is mounted for the life of the page and never has its text
   * changed, which is the point: `Plate` solves its size by BISECTING on
   * measured line boxes, and a solve is several real renders at several font
   * sizes. Swapping the text of one live plate makes the visitor watch that
   * search happen. Mounting each slide once moves every search to first paint,
   * where it belongs, so a slide change is a pure compositor crossfade with no
   * layout in it at all.
   */
  const layerTransition = prefersReducedMotion
    ? undefined
    : "opacity var(--ls-motion-expand-dur) var(--ls-motion-expand-ease)";

  const chevronStyle: React.CSSProperties = {
    width: "var(--tls-target-min)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    // Ink, never accent: navigation is not one of the accent's six roles.
    color: "var(--tls-hero-fg)",
    background: "transparent",
    // 42% ink, and deliberately QUIETER than §03.4's full-ink ring on the
    // transport two rows up. The two used to match, which is how the panel's
    // one real affordance ended up carrying the same weight as its paging —
    // the transport went to TPlay in TES-2344 and took the spec ring with it.
    // These stay a rung down: they move the carousel, they do not play it.
    border: "1px solid color-mix(in srgb, var(--tls-hero-fg) 42%, transparent)",
    borderRadius: 999,
    cursor: "pointer",
  };

  return (
    <>
      <section
        id="home"
        className="tls-clears-nav relative isolate overflow-hidden"
        style={{
          background: "var(--tls-hero-bg)",
          color: "var(--tls-hero-fg)",
          borderBottom: "1px solid var(--tls-hero-edge)",
          "--tls-focus-ring": "var(--tls-hero-focus)",
        } as React.CSSProperties}
      >
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          {slides.map((slide, slideIndex) => {
            const media = resolveHomeHeroMedia(slide);
            if (!media) return null;

            // `priority`, `fetchPriority` and `loading` are keyed to EACH
            // SLIDE'S OWN index, never to the slide currently on screen: only
            // the first slide is the LCP candidate, and re-keying on rotation
            // would flip the attributes on live <img> elements to no effect.
            // The four backgrounds behind it are eager low-priority <img>s, so
            // they are in hand before the first auto-advance at ~9s. They get
            // no <link rel="preload">: `preload()` called from this client
            // component re-runs on hydration, and the client's getImageProps
            // has no deployment id, so it emitted a second, differently keyed
            // URL per slide that no <img> ever used (Chrome: "preloaded but
            // not used within a few seconds", five times per load).
            const isFirst = slideIndex === 0;

            return (
              <div
                key={slide.id}
                data-ls
                className="absolute inset-0"
                style={{
                  opacity: slideIndex === index ? 1 : 0,
                  transition: layerTransition,
                }}
              >
                <Image
                  src={media.src}
                  alt=""
                  fill
                  sizes="100vw"
                  // No `priority` on any slide. Next's `priority` emits a
                  // <link rel="preload"> from BOTH the server render and the
                  // client re-render, and the client copy has no deployment id,
                  // so on Vercel it keys a different URL than the <img> and
                  // Chrome logs "preloaded but not used". Slide 0 is already
                  // an eager <img> near the top of the server HTML, so the
                  // parser finds it without a preload; `fetchPriority="high"`
                  // is what puts it ahead of the siblings (Next 16 does not
                  // derive it from `priority` either). The siblings say "low"
                  // on the <img>: an eager <img> in the viewport otherwise
                  // upgrades itself to High on layout and races slide 0.
                  fetchPriority={isFirst ? "high" : "low"}
                  loading="eager"
                  className="object-cover"
                  style={{ objectPosition: media.objectPosition }}
                />
              </div>
            );
          })}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(90deg, color-mix(in srgb, var(--tls-hero-bg) 96%, transparent) 0%, color-mix(in srgb, var(--tls-hero-bg) 88%, transparent) 48%, color-mix(in srgb, var(--tls-hero-bg) 38%, transparent) 100%), linear-gradient(180deg, color-mix(in srgb, var(--tls-hero-bg) 58%, transparent) 0%, color-mix(in srgb, var(--tls-hero-bg) 20%, transparent) 48%, color-mix(in srgb, var(--tls-hero-bg) 72%, transparent) 100%)",
            }}
          />
        </div>
        <div
          className={`${PANEL_CONTAINER_CLASS} relative z-10 pb-10 lg:pb-12`}
          style={PANEL_CONTAINER_STYLE}
          // SC 2.2.2 holds: the pointer resting on the hero, and (below)
          // keyboard focus landing inside it, both stop the clock.
          onPointerEnter={() => setPointerInside(true)}
          onPointerLeave={() => setPointerInside(false)}
          onFocus={rotates ? engage : undefined}
          // Explicit, not a default: an auto-advancing region that announces
          // itself turns a screen reader into a metronome. Only user-driven
          // changes reach the polite status node further down.
          aria-live="off"
          {...(rotates
            ? {
                role: "group",
                "aria-roledescription": "carousel",
                "aria-label": pinnedSlide ? "Featured and most downloaded sounds" : "Most downloaded sounds",
              }
            : null)}
        >
          {/*
            The page's ONE real `<h1>`. It does not rotate and its text is
            not any sound's title. Both a11y and SEO tooling flag a page
            whose only h1 disappears (behind `aria-hidden`) for 4 of every 5
            dwell states, which is what happens when the heading rides the
            slide. This is a genuine description of what the page is, not a
            restatement of the visible plate underneath it, so a screen
            reader user does not hear the same string announced twice back to
            back. It is visually hidden, not removed: the giant rotating
            plate is still the on-screen signature the design calls for.
          */}
          <h1 className="sr-only">
            Tesla lock chime sounds: preview, download, and install as LockChime.wav
          </h1>
          {/* One plain line saying what the site is, read before the sound name
              at every width. */}
          <p className="mb-0 max-w-[36ch] text-base lg:mb-2 lg:max-w-none font-semibold leading-snug text-[var(--tls-hero-fg)]">
            Free Tesla lock sounds. Play one, download {TESLA_FACTS.usb.filename}.
          </p>
          {/* The slide stack: one grid cell, every slide in it. */}
          <div style={{ display: "grid" }}>
            {slides.map((slide, slideIndex) => {
              const active = slideIndex === index;
              const isPinned = pinnedSlide?.soundId === slide.id;
              const downloads = slide.downloads ? downloadCountAriaLabel(slide.downloads) : null;
              const rank = downloadRankPhrase(downloadRanks?.[slide.id]);
              const heroLine = isPinned
                ? [pinnedSlide?.kicker, downloads].filter(Boolean).join(", ")
                : downloads && [downloads, rank].filter(Boolean).join(", ");
              const playingThis = player.isPlaying && currentPlayingId === slide.id;
              const loadingThis = player.isLoading && currentPlayingId === slide.id;
              const isCurrent = currentPlayingId === slide.id;
              // The catalog length first, so the readout does not change when
              // playback starts and the element reports its decoded length.
              const activeDuration = slide.duration || (isCurrent ? player.duration : 0) || 0;

              return (
                <div
                  key={slide.id}
                  // `data-ls` is what declares the --ls-* token layer on this
                  // subtree; without it `var(--ls-motion-expand-dur)` below
                  // resolves to nothing, the shorthand is invalid, and the
                  // crossfade silently degrades to a hard cut. Measured, not
                  // assumed — that is exactly what happened first time.
                  // It also brings the media-query collapse to 0ms under
                  // reduced motion, alongside the JS check.
                  data-ls
                  className="pt-0 sm:pt-2"
                  style={{
                    gridArea: "1 / 1",
                    // Every slide (including the four not on screen) sits in
                    // this same implicit grid track, and a plain grid item's
                    // automatic minimum width is its content's min-content
                    // size, not 0 — so an off-screen slide's title can inflate
                    // the shared track and push the VISIBLE slide's plate past
                    // the viewport edge. Invisible until the Plate fix in
                    // plate.tsx started rendering with `overflow-wrap: normal`
                    // (§ mid-word-break fix): under the old `anywhere` value
                    // every title's min-content width was ~1 glyph, so this
                    // was never exercised. `minWidth: 0` opts this item out of
                    // that auto-sizing, matching how a track-filling grid item
                    // is meant to behave.
                    minWidth: 0,
                    opacity: active ? 1 : 0,
                    transition: layerTransition,
                    pointerEvents: active ? undefined : "none",
                  }}
                  aria-hidden={active ? undefined : true}
                  inert={!active}
                  {...(rotates
                    ? {
                        role: "group",
                        "aria-roledescription": "slide",
                        "aria-label": `Sound ${slideIndex + 1} of ${slideCount}`,
                      }
                    : null)}
                >
                  {/*
                    Outer box carries the reservation; inner box is what gets
                    measured, so the number can shrink as well as grow.

                    Bottom-aligned: plate, descriptor and the count line move
                    as one block, so the slack left by a short title becomes
                    top padding under the nav rather than a hole between the
                    title and the player. Every gap inside the hero is constant
                    on every slide.
                  */}
                  <div
                    style={{
                      minHeight: reservedPlateHeight || undefined,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-end",
                    }}
                  >
                    <div
                      ref={node => {
                        plateBoxRefs.current[slideIndex] = node;
                      }}
                    >
                      <HeroPlate
                        title={slide.title}
                        level={active && playingThis ? heroLevel : undefined}
                      />
                      {/* Plain text under the plate, never a label above it
                          (owner ruling 2026-10-02): why this sound is here
                          and its count, or its count and true rank. */}
                      {heroLine ? (
                        <Mono size={14} color="var(--tls-hero-muted)" style={{ display: "block", marginTop: "var(--tls-space-sm)" }}>
                          {heroLine}
                        </Mono>
                      ) : null}
                    </div>
                  </div>

                  <div
                    className="mt-7 flex items-center gap-3 sm:gap-5"
                    aria-label={`Preview ${slide.title}`}
                  >
                    {/* §03.4's component at the ladder's 56px rung, on the
                      "hero" ground. This was a fifth hand-rolled copy of the
                      play control until TES-2344: the one the TES-2312 audit
                      never counted, because `TPlayGround` had no name for a
                      panel that is neither the page canvas nor §04.2's inverted
                      plate. It has one now, and it reads the same `--tls-hero-*`
                      tokens this copy did, so the playing disc keeps the
                      authored lift that clears 1.4.11 on the dark hero plate.

                      The copy was also `disabled` until hydration, which blurs
                      an early keyboard user to the body. `loading` reaches
                      aria-busy only, so the control stays focusable and simply
                      has no handler yet.

                      At rest the disc is §03.4's solid ink, the same as every
                      other play control (owner ruling, 2026-10-03, which
                      retired the hollow "not loaded yet" ring here too). */}
                    <TPlay
                      size={56}
                      ground="hero"
                      playing={playingThis}
                      loading={loadingThis || !isHydrated}
                      title={slide.title}
                      onClick={isHydrated ? () => handlePlay(slide) : undefined}
                    />
                    <div className="min-w-0 flex-1">
                      {/* The strip becomes the scrub bar once this slide is the
                          current sound; before that it is a drawing with no tab
                          stop, so Play stays the only way in. Stored peaks only. */}
                      <PlayerSeek
                        ground="hero"
                        className="h-11"
                        peaks={slide.waveform_data}
                        duration={activeDuration}
                        currentTime={isCurrent ? player.currentTime || 0 : 0}
                        interactive={isCurrent}
                        playhead={isCurrent}
                        title={slide.title}
                        decimals={1}
                        onSeekCommit={player.seek}
                      />
                    </div>
                    <span className="whitespace-nowrap text-sm font-semibold text-(--tls-hero-muted) tabular-nums">
                      {isCurrent ? (
                        <>
                          <PlayerTime inheritFont currentTime={player.currentTime || 0} duration={activeDuration} />
                          {" / "}
                        </>
                      ) : null}
                      {activeDuration ? <PlayerTime inheritFont className="min-w-0" type="duration" duration={activeDuration} /> : "preview"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Only ever written by a user-driven change, which is the other half
              of aria-live="off" on the region above. */}
          <div role="status" aria-live="polite" className="sr-only">
            {announcement}
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            {sound ? (
              // The hero's ONE primary, through the shared <Button> primitive
              // (§02.1: 44 tall, 12/32 padding, 14/700, press scale .97 +
              // opacity .85). The fill is picked against the authored hero
              // ground through --tls-hero-accent.
              //
              // The row lives outside the crossfaded stack on purpose: its
              // rendered content is identical on every slide (only the href
              // moves), so fading it would animate text into itself, and the
              // navigation cluster at the end of the row must never fade —
              // fading the control you just pressed is exactly the "motion
              // performs" failure principle 6 refuses.
              <Button
                asChild
                className="bg-[var(--tls-hero-accent)] text-[var(--tls-hero-accent-fg)] hover:bg-[var(--tls-hero-accent-hover)] motion-reduce:active:bg-[var(--tls-hero-accent-hover)] font-bold"
              >
                {/* No trailing arrow, deliberately, and it must not come back:
                    the arrow reads as onward navigation to another surface,
                    which is what "Browse the library" below does. This button
                    acts on the sound already named in the plate directly above
                    it, so it needs no such affordance, and carrying one on
                    both halves of the pair diluted the hierarchy between them. */}
                <Link
                  href={soundUrl(sound)}
                  onClick={engage}
                  data-cuelume-press="press"
                  data-cuelume-release="release"
                >
                  Get this sound
                </Link>
              </Button>
            ) : null}
            {/* §02.1 secondary: the filled primary above and this outlined
                control are ONE pair, so this is the same <Button> primitive
                (44 tall, 12/32 padding, pill radius, press scale .97, DS
                ring) with variant="secondary", not a text link. The variant's
                own colours are ink-on-surface, so the secondary uses the
                hero's own foreground and ground tokens rather than an
                inversion-specific fallback. */}
            <Button
              asChild
              variant="secondary"
              className="border-[color:var(--tls-hero-fg)] text-[var(--tls-hero-fg)] hover:bg-[var(--tls-hero-fg)] hover:text-[var(--tls-hero-bg)] motion-reduce:active:bg-[var(--tls-hero-fg)] motion-reduce:active:text-[var(--tls-hero-bg)] font-semibold"
            >
              <Link
                href="/sounds"
                onClick={engage}
                data-cuelume-press="press"
                data-cuelume-release="release"
              >
                Browse the library
              </Link>
            </Button>

            {/*
              LOAD-BEARING, do not "tidy" this: these two chevrons ARE the
              WCAG 2.2.2 (Pause, Stop, Hide) mechanism for the hero. There is
              no separate pause button, so activating either one is what stops
              the auto-advance, permanently, for the rest of the session, via
              the engagement latch in useHeroRotation. Removing the stop, or
              making it temporary, takes the page out of conformance.

              The other holds are still live and also load-bearing: pointer
              over the hero, keyboard focus inside it, a hidden tab, hero audio
              playing, and prefers-reduced-motion disabling auto-advance
              outright.

              Placed at the END of the row, on the shared .tls-container axis,
              rather than overlaid on the panel's left and right edges. The
              plate is 100px+ type running nearly the full container, so
              edge-anchored chevrons would sit on the letterforms, and the
              first rule of this system is that the chrome is quiet while the
              content is loud. After the CTAs in DOM order too, so the primary
              action is reached first when tabbing.

              Below `sm` it takes its own line (still on the axis, still left
              aligned) because the two CTA pills plus a cluster do not fit a
              390px viewport without a bad wrap.
            */}
            {rotates ? (
              <div
                className="mt-2 flex w-full items-center gap-2 sm:mt-0 sm:ml-auto sm:w-auto"
                role="group"
                aria-label="Change sound"
              >
                <button
                  type="button"
                  onClick={rotation.prev}
                  data-cuelume-toggle="page"
                  className="tlsr-focus tls-target"
                  style={chevronStyle}
                  aria-label="Previous sound"
                >
                  <ChevronLeft aria-hidden="true" size={18} />
                </button>
                <button
                  type="button"
                  onClick={rotation.next}
                  data-cuelume-toggle="page"
                  className="tlsr-focus tls-target"
                  style={chevronStyle}
                  aria-label="Next sound"
                >
                  <ChevronRight aria-hidden="true" size={18} />
                </button>
                <Mono
                  size={11}
                  color="var(--tls-hero-muted)"
                  style={{ opacity: 0.74, paddingInline: 4, fontVariantNumeric: "tabular-nums" }}
                >
                  {index + 1} / {slideCount}
                </Mono>
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </>
  );
}
